import { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Link,
  Stack,
  Table,
  Text,
} from '@chakra-ui/react';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'https://dcfteacher.com').replace(
  /\/+$/,
  '',
);
const OPENAPI_URL = `${API_ORIGIN}/api/v1/openapi.json`;

const ENDPOINTS = [
  { method: 'GET', path: '/openapi.json', tier: 'Public', description: 'OpenAPI 3.0 spec' },
  { method: 'GET', path: '/stocks/search?q=', tier: 'Free', description: 'Search tickers / names' },
  { method: 'GET', path: '/sectors', tier: 'Free', description: 'Sector list with counts' },
  { method: 'GET', path: '/stocks/{ticker}', tier: 'Free', description: 'Ticker metadata' },
  {
    method: 'GET',
    path: '/stocks/{ticker}/financials',
    tier: 'Free',
    description: 'Financial statements',
  },
  { method: 'GET', path: '/stocks/{ticker}/filings', tier: 'Free', description: 'SEC filings' },
  { method: 'GET', path: '/stocks/{ticker}/dcf', tier: 'Premium', description: 'DCF defaults' },
  {
    method: 'GET',
    path: '/stocks/{ticker}/reverse-dcf',
    tier: 'Premium',
    description: 'Reverse DCF analysis',
  },
  {
    method: 'GET',
    path: '/stocks/{ticker}/8k-summary',
    tier: 'Premium',
    description: 'Recent 8-K AI summaries',
  },
  {
    method: 'GET',
    path: '/stocks/{ticker}/charts',
    tier: 'Premium',
    description: 'Chart data bundle',
  },
];

const MCP_TOOLS = [
  { name: 'search_stocks', tier: 'Free', description: 'Search by ticker/name' },
  { name: 'list_sectors', tier: 'Free', description: 'Sector catalog' },
  { name: 'get_stock_info', tier: 'Free', description: 'Stock metadata' },
  { name: 'get_stock_financials', tier: 'Free', description: 'Financial statements' },
  { name: 'get_stock_filings', tier: 'Free', description: 'SEC filings' },
  { name: 'get_stock_dcf', tier: 'Premium', description: 'DCF defaults' },
  { name: 'get_stock_reverse_dcf', tier: 'Premium', description: 'Reverse DCF' },
  { name: 'get_stock_8k_summary', tier: 'Premium', description: '8-K AI summaries' },
  { name: 'get_stock_charts', tier: 'Premium', description: 'Chart data' },
];

const curlExample = `curl -sS \\
  -H "Authorization: Bearer $DCF_TEACHER_API_KEY" \\
  "${API_ORIGIN}/api/v1/stocks/AAPL/financials"`;

const pythonExample = `import os
import requests

API_KEY = os.environ["DCF_TEACHER_API_KEY"]
BASE = "${API_ORIGIN}/api/v1"

resp = requests.get(
    f"{BASE}/stocks/AAPL/filings",
    headers={"Authorization": f"Bearer {API_KEY}"},
    timeout=60,
)
resp.raise_for_status()
print(resp.json()["count"])`;

const jsExample = `const res = await fetch(
  "${API_ORIGIN}/api/v1/stocks/search?q=apple",
  { headers: { Authorization: \`Bearer \${process.env.DCF_TEACHER_API_KEY}\` } },
);
const data = await res.json();
console.log(data.results);`;

const mcpConfigExample = `{
  "mcpServers": {
    "dcf-teacher": {
      "command": "python",
      "args": ["/absolute/path/to/dcf-teacher/backend/mcp_server.py"],
      "env": {
        "DCF_TEACHER_API_KEY": "dcf_your_key_here",
        "DCF_TEACHER_API_BASE": "${API_ORIGIN}"
      }
    }
  }
}`;

const SECTIONS = [
  { id: 'authentication', label: 'Authentication' },
  { id: 'rate-limits', label: 'Rate limits' },
  { id: 'endpoints', label: 'Endpoints' },
  { id: 'examples', label: 'Examples' },
  { id: 'mcp', label: 'MCP setup' },
  { id: 'openapi', label: 'OpenAPI' },
];

function CodeBlock({ code, label }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Box>
      {label && (
        <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={2}>
          {label}
        </Text>
      )}
      <Box position="relative">
        <Box
          as="pre"
          fontFamily="mono"
          fontSize="sm"
          bg="gray.900"
          color="gray.100"
          p={4}
          pr={20}
          borderRadius="md"
          overflowX="auto"
          whiteSpace="pre-wrap"
          wordBreak="break-word"
        >
          {code}
        </Box>
        <Button
          size="xs"
          position="absolute"
          top={2}
          right={2}
          variant="outline"
          colorPalette="gray"
          bg="gray.800"
          color="gray.100"
          borderColor="gray.600"
          onClick={handleCopy}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </Box>
    </Box>
  );
}

function Section({ id, title, children }) {
  return (
    <Box as="section" id={id} scrollMarginTop="calc(var(--nav-h) + 16px)">
      <Stack gap={4}>
        <Heading as="h2" size="lg">
          {title}
        </Heading>
        {children}
      </Stack>
    </Box>
  );
}

function ApiDocs() {
  return (
    <Box maxW="960px" mx="auto" py={8} px={{ base: 4, md: 6 }}>
      <Stack gap={10}>
        <Box>
          <Heading as="h1" size="2xl" mb={3}>
            API &amp; MCP Docs
          </Heading>
          <Text color="gray.600" fontSize="lg" maxW="720px">
            Query DCF Teacher stock data from scripts, apps, and AI agents. Create an API key in
            Settings, then call the versioned REST API or connect via Model Context Protocol.
          </Text>
          <Flex mt={5} gap={3} wrap="wrap">
            <Button
              as={Link}
              href="/settings"
              colorPalette="teal"
              textDecoration="none"
              _hover={{ textDecoration: 'none' }}
            >
              Create API key
            </Button>
            <Button
              as={Link}
              href={OPENAPI_URL}
              target="_blank"
              rel="noreferrer"
              variant="outline"
              colorPalette="teal"
              textDecoration="none"
              _hover={{ textDecoration: 'none' }}
            >
              OpenAPI JSON
            </Button>
          </Flex>
        </Box>

        <Flex as="nav" aria-label="Documentation sections" gap={3} wrap="wrap">
          {SECTIONS.map((section) => (
            <Link
              key={section.id}
              href={`#${section.id}`}
              color="teal.700"
              fontSize="sm"
              fontWeight="medium"
            >
              {section.label}
            </Link>
          ))}
        </Flex>

        <Section id="authentication" title="Authentication">
          <Text color="gray.700">
            1. Sign in and open <Link href="/settings" color="teal.700">Settings</Link>.
          </Text>
          <Text color="gray.700">2. Create an API key (shown once).</Text>
          <Text color="gray.700">3. Send it on every request:</Text>
          <CodeBlock code="Authorization: Bearer dcf_your_key_here" label="Header" />
        </Section>

        <Section id="rate-limits" title="Rate limits">
          <Text color="gray.700">
            Free accounts get 100 requests/day (UTC), shared across all API keys for that
            account. Premium accounts have unlimited daily usage and unlock analysis
            endpoints. All tiers also have short-window burst limits: 60 requests/minute
            per IP, 30/minute per free key, and 120/minute per premium key. Daily responses
            include <Text as="span" fontFamily="mono">X-RateLimit-Limit</Text>{' '}
            and <Text as="span" fontFamily="mono">X-RateLimit-Remaining</Text> when a daily
            limit applies. Burst limits return HTTP 429 with{' '}
            <Text as="span" fontFamily="mono">Retry-After</Text>.
          </Text>
          <Box overflowX="auto">
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Tier</Table.ColumnHeader>
                  <Table.ColumnHeader>Daily limit</Table.ColumnHeader>
                  <Table.ColumnHeader>Burst</Table.ColumnHeader>
                  <Table.ColumnHeader>Access</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                <Table.Row>
                  <Table.Cell>Free</Table.Cell>
                  <Table.Cell>100 / day</Table.Cell>
                  <Table.Cell>30 / min per key</Table.Cell>
                  <Table.Cell>Search, sectors, stock info, financials, filings</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>Premium</Table.Cell>
                  <Table.Cell>Unlimited</Table.Cell>
                  <Table.Cell>120 / min per key</Table.Cell>
                  <Table.Cell>Free endpoints + DCF, reverse DCF, 8-K summaries, charts</Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table.Root>
          </Box>
        </Section>

        <Section id="endpoints" title="Endpoints">
          <Text color="gray.700">
            Base path: <Text as="span" fontFamily="mono">/api/v1</Text>
          </Text>
          <Box overflowX="auto">
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Method</Table.ColumnHeader>
                  <Table.ColumnHeader>Path</Table.ColumnHeader>
                  <Table.ColumnHeader>Tier</Table.ColumnHeader>
                  <Table.ColumnHeader>Description</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {ENDPOINTS.map((endpoint) => (
                  <Table.Row key={endpoint.path}>
                    <Table.Cell fontFamily="mono">{endpoint.method}</Table.Cell>
                    <Table.Cell fontFamily="mono">{endpoint.path}</Table.Cell>
                    <Table.Cell>{endpoint.tier}</Table.Cell>
                    <Table.Cell>{endpoint.description}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        </Section>

        <Section id="examples" title="Examples">
          <Stack gap={6}>
            <CodeBlock code={curlExample} label="curl" />
            <CodeBlock code={pythonExample} label="Python" />
            <CodeBlock code={jsExample} label="JavaScript" />
          </Stack>
        </Section>

        <Section id="mcp" title="MCP setup">
          <Text color="gray.700">
            Use DCF Teacher from Claude Desktop, Cursor, Windsurf, and other MCP clients. Create an
            API key first, then point your client at <Text as="span" fontFamily="mono">backend/mcp_server.py</Text>.
          </Text>
          <CodeBlock code={mcpConfigExample} label="Claude Desktop / Cursor config" />
          <Text color="gray.700">
            Environment variables: <Text as="span" fontFamily="mono">DCF_TEACHER_API_KEY</Text> (required)
            and optional <Text as="span" fontFamily="mono">DCF_TEACHER_API_BASE</Text> (defaults to{' '}
            <Text as="span" fontFamily="mono">https://dcfteacher.com</Text>).
          </Text>
          <Box overflowX="auto">
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Tool</Table.ColumnHeader>
                  <Table.ColumnHeader>Tier</Table.ColumnHeader>
                  <Table.ColumnHeader>Description</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {MCP_TOOLS.map((tool) => (
                  <Table.Row key={tool.name}>
                    <Table.Cell fontFamily="mono">{tool.name}</Table.Cell>
                    <Table.Cell>{tool.tier}</Table.Cell>
                    <Table.Cell>{tool.description}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        </Section>

        <Section id="openapi" title="OpenAPI">
          <Text color="gray.700">
            Machine-readable schema:{' '}
            <Link href={OPENAPI_URL} color="teal.700" target="_blank" rel="noreferrer">
              /api/v1/openapi.json
            </Link>
          </Text>
          <CodeBlock code={`curl -sS ${OPENAPI_URL}`} label="Fetch OpenAPI" />
        </Section>
      </Stack>
    </Box>
  );
}

export default ApiDocs;
